import { dbg } from '../utils/debugFlag'
/**
 * BundleDetector — coordinated-buying (bundle) detector. SHADOW MODE.
 * ─────────────────────────────────────────────────────────────────────
 * The problem it exists for: bundlers split a buy across many wallets to fake
 * organic interest and inflate a coin's apparent demand (and its Jupiter organic
 * score). Real interest is MESSY — different wallets, different sizes, arriving
 * at different moments. A bundle is the opposite: a burst of near-identical buys
 * from many wallets in a tight window. That texture is visible directly in the
 * swap stream, and unlike Jupiter's organicScore (a 30s-lagging poll) it's
 * measurable per-swap, in seconds.
 *
 * This is ArdDetector's sibling — same dependency-free, field-mismatch-immune
 * design (sol ?? amount, mcap ?? tradeMcap), same "name what fires" logging so
 * tuning is done on evidence. Phase 1 is SHADOW: it computes a coordination
 * score every tick and console-logs what it WOULD alert, and fires nothing to
 * the UI. Run it against real tape for a session, read the logs, tune the
 * thresholds, THEN wire the footer alert. (Exactly how the base engine and Ard
 * were built — thresholds tuned on shadow output before they earned screen space
 * or a trade.)
 *
 * WHAT COORDINATION MEANS HERE — three signals off a rolling window, because no
 * single one is sufficient and each has an innocent explanation on its own:
 *
 *   1. WALLET MULTIPLICITY   many distinct wallets in a short burst. On its own
 *                            this is just "a popular coin" — organic too.
 *   2. SIZE UNIFORMITY       those buys are near-identical in size. On its own
 *                            this is just "everyone aped the same round number".
 *   3. TIME CLUSTERING       they all landed in a few seconds. On its own this
 *                            is just "a caller posted it".
 *
 * Any ONE is normal. All THREE at once — a dozen+ wallets buying the same tiny
 * amount within a couple seconds — is a bundle's fingerprint and almost nothing
 * else. The score multiplies the three so a low value on any single axis keeps
 * the total low; it only spikes when they align.
 *
 * DELIBERATELY BUY-SIDE. Bundling is a demand-fabrication play; the coordinated
 * event we care about is coordinated BUYING. Sells are watched only to compute
 * the active baseline, not scored.
 */

export class BundleDetector {
  constructor(config = {}) {
    this.config = {
      windowSec:        config.windowSec        ?? 8,     // the burst window we score
      minBuys:          config.minBuys          ?? 6,     // fewer than this = nothing to judge
      minWallets:       config.minWallets       ?? 5,     // distinct buyers in-window to consider
      // Size uniformity: coefficient of variation (stdev/mean) of buy sizes.
      // LOW = uniform = suspicious. Organic scatter runs high.
      uniformCvMax:     config.uniformCvMax     ?? 0.35,  // CV at/under this reads as "uniform"
      // Wallet freshness bonus: a burst of wallets we've NEVER seen on this
      // token before is more suspicious than regulars trading.
      freshnessWeight:  config.freshnessWeight  ?? 1.0,
      // Score threshold a burst must clear to be logged as a would-fire.
      scoreThreshold:   config.scoreThreshold   ?? 0.6,
      // Debounce: after a would-fire, stay quiet this long. A bundle is one
      // event; without this the same burst logs every frame for `windowSec`.
      cooldownSec:      config.cooldownSec       ?? 15,
      logAll:           config.logAll            ?? false, // true = log EVERY tick's score (loud; for calibration)
      ...config,
    }
    this.onWouldFire = null   // ({ token, score, wallets, buys, spanSec, cvSize, freshFrac, mcap })
    // wallet → first-seen ts (ms) on THIS token. A Map, not a Set, and the
    // difference is the whole freshness axis: update() runs EVERY FRAME, so a
    // Set marked a burst's wallets "seen" one frame after they arrived and
    // freshness self-destructed mid-burst (the burst that scored 1.08 in a
    // one-shot test was SILENT fed frame-by-frame). With first-seen timestamps,
    // a wallet counts as fresh for windowSec after its first sighting — burst-
    // stable — and reads as a regular after that.
    this._seenWallets = new Map()
    this._lastFireAt = 0
    this._token = null
  }

  // Reset per-token: freshness and cooldown are meaningless across a token switch.
  resetForToken(token) {
    if (token === this._token) return
    this._token = token
    this._seenWallets.clear()
    this._lastFireAt = 0
  }

  // Field-mismatch-immune accessors — same split that silently zeroed the base
  // engine's churn on the server (sol on client, amount on server).
  static vol(sw)    { return Number(sw.sol ?? sw.amount) || 0 }
  static px(sw)     { return Number(sw.mcap ?? sw.tradeMcap) || 0 }
  static t(sw)      { return Number(sw.ts ?? sw.time) || 0 }
  static wallet(sw) { return sw.wallet || sw.trader || sw.owner || null }

  /**
   * update(ctx) — call once per tick.
   * ctx: { swaps, mcap, token }
   *   swaps: rolling raw-swap window (chronological), each { side, amount|sol,
   *          mcap|tradeMcap, wallet, ts|time }
   */
  update(ctx) {
    const { swaps, mcap, token } = ctx
    if (token) this.resetForToken(token)
    if (!Array.isArray(swaps) || swaps.length === 0) return

    // TAPE CLOCK: ctx.now = server-aligned time (browser passes serverNow so a
    // skewed local clock can't shift the window off the trade timestamps).
    // Absent (droplet runner) → Date.now(), which is NTP-synced there.
    const now = Number.isFinite(ctx.now) ? ctx.now : Date.now()
    const V = BundleDetector.vol, T = BundleDetector.t, W = BundleDetector.wallet
    const cfg = this.config
    const cut = now - cfg.windowSec * 1000

    // Collect in-window BUYS. (Sells ignored — see class note.)
    const sizes = []
    const wallets = new Set()
    let tMin = Infinity, tMax = -Infinity
    let fresh = 0
    for (let i = swaps.length - 1; i >= 0; i--) {
      const ts = T(swaps[i])
      if (ts < cut) break
      if (swaps[i].side !== 'buy') continue
      const v = V(swaps[i])
      if (!(v > 0)) continue
      const w = W(swaps[i])
      sizes.push(v)
      if (w && !wallets.has(w)) {
        wallets.add(w)
        const first = this._seenWallets.get(w)
        if (first === undefined) { this._seenWallets.set(w, ts); fresh++ }  // first-ever sighting
        else if (first >= cut) fresh++    // first sighted within THIS window → still fresh
      }
      if (ts < tMin) tMin = ts
      if (ts > tMax) tMax = ts
    }

    const buys = sizes.length
    const nWallets = wallets.size

    // Gate: not enough to judge. First-sighting timestamps were already recorded
    // inline above, so a slow drip still ages out of freshness naturally.
    if (buys < cfg.minBuys || nWallets < cfg.minWallets) {
      if (cfg.logAll) dbg('[bundle] (below gate)', { buys, nWallets, need: `${cfg.minBuys}buys/${cfg.minWallets}wallets` })
      return
    }

    // ── Signal 1: wallet multiplicity, normalized. More distinct buyers = more
    //    coordination potential. Saturates so 30 wallets isn't 6x scarier than 5.
    const multiplicity = Math.min(1, nWallets / 20)

    // ── Signal 2: size uniformity via coefficient of variation. Low CV = tight
    //    cluster of near-identical buys = suspicious. Map so CV<=uniformCvMax → 1,
    //    and it decays to 0 as scatter grows.
    const mean = sizes.reduce((a, b) => a + b, 0) / buys
    const variance = sizes.reduce((a, b) => a + (b - mean) * (b - mean), 0) / buys
    const cv = mean > 0 ? Math.sqrt(variance) / mean : 1
    const uniformity = Math.max(0, Math.min(1, 1 - (cv - cfg.uniformCvMax) / cfg.uniformCvMax))

    // ── Signal 3: time clustering. The tighter the span relative to the window,
    //    the more it looks like one coordinated release vs steady interest.
    const spanSec = (tMax - tMin) / 1000
    const clustering = Math.max(0, 1 - spanSec / cfg.windowSec)

    // ── Freshness multiplier: a burst of first-time wallets is worse than
    //    regulars. Scales 1.0 → (1 + freshnessWeight) as the fresh fraction → 1.
    const freshFrac = nWallets > 0 ? fresh / nWallets : 0
    const freshnessMult = 1 + cfg.freshnessWeight * freshFrac

    // Multiplicative: any weak axis keeps the score low. Only alignment spikes it.
    const score = multiplicity * uniformity * clustering * freshnessMult

    const detail = {
      token, score: +score.toFixed(3), wallets: nWallets, buys,
      spanSec: +spanSec.toFixed(1), cvSize: +cv.toFixed(2),
      freshFrac: +freshFrac.toFixed(2), mcap: mcap || null,
      parts: { multiplicity: +multiplicity.toFixed(2), uniformity: +uniformity.toFixed(2), clustering: +clustering.toFixed(2), freshnessMult: +freshnessMult.toFixed(2) },
    }

    if (cfg.logAll) dbg('[bundle] tick', detail)

    if (score >= cfg.scoreThreshold) {
      if (now - this._lastFireAt < cfg.cooldownSec * 1000) return  // debounced
      this._lastFireAt = now
      // SHADOW: log the would-fire. Phase 2 swaps this for the footer alert.
      dbg('%c[bundle] WOULD-FIRE', 'color:#f0a72b;font-weight:bold', detail)
      this.onWouldFire?.(detail)
    }
  }
}

export default BundleDetector